/**
 * The record behind a check that blocked someone's merge (#801).
 *
 * Every fitness script SystemDox publishes prints this page's URL when it fails,
 * so this is the first — often only — SystemDox screen the governed engineer
 * ever sees. It answers their three questions in order: what blocked me, why
 * does this rule exist, and what do I do if it is wrong.
 *
 * It is also the Checks full editor (specs/changes/consolidated-full-editor
 * R6): the same shell as Requirements/Design, opening in preview with Edit,
 * autosave and per-check Publish to CI for the people who own the standard.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ShieldAlert, ArrowLeft, AlertTriangle, MessageSquareWarning,
  CheckCircle2, XCircle, ExternalLink, Clock,
} from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { guardrailsApi, type GuardrailEntry, type CiSettings } from '@/services/guardrailsApi';
import { fitnessApi, toCiFailure, describeFailures, type CheckResult } from '@/services/fitnessApi';
import { GuardrailEntryView } from '@/components/guardrails/GuardrailEntryView';
import { EditableGuardrailEntry, type GuardrailEntryData } from '@/components/guardrails/EditableGuardrailEntry';
import { FullEditorShell } from '@/components/editor/FullEditorShell';
import type { EditorMode } from '@/components/editor/DocContentEditor';
import { useAutosave } from '@/hooks/useAutosave';
import { cn } from '@/lib/utils';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'text-red-600 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  high: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
  medium: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  low: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
};

interface Occurrence {
  repo: string;
  check: CheckResult;
}

type PublishState =
  | { phase: 'idle' }
  | { phase: 'running' }
  | { phase: 'done'; prUrl?: string }
  | { phase: 'error'; message: string };

export default function GuardrailRecordPage() {
  const { guardrailId = '' } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [entry, setEntry] = useState<GuardrailEntry | null>(null);
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState<EditorMode>('preview');
  const [draft, setDraft] = useState<GuardrailEntryData | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [ciSettings, setCiSettings] = useState<CiSettings | null>(null);
  const [publish, setPublish] = useState<PublishState>({ phase: 'idle' });

  useEffect(() => {
    let cancelled = false;

    guardrailsApi
      .getEntry(guardrailId)
      .then((e) => { if (!cancelled) setEntry(e); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Where this rule is firing right now — the difference between "a policy
    // exists" and "a policy is blocking this person's branch".
    fitnessApi
      .getResults()
      .then((data) => {
        if (cancelled) return;
        setOccurrences(
          data.reports.flatMap((r) =>
            r.checks
              .filter((c) => c.guardrailId === guardrailId)
              .map((c) => ({ repo: r.repo, check: c }))
          )
        );
      })
      .catch(() => { /* the record still stands on its own without live results */ });

    // Per-check Publish to CI only renders once a fitness-tests repo exists.
    guardrailsApi
      .getCiSettings()
      .then((s) => { if (!cancelled) setCiSettings(s); })
      .catch(() => { /* publish simply stays hidden */ });

    return () => { cancelled = true; };
  }, [guardrailId]);

  const handleSave = useCallback(async () => {
    if (!entry || !draft) return;
    try {
      const updated = await guardrailsApi.updateEntry(entry.id, draft);
      setEntry(updated);
      setSaveError(null);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setSaveError(status === 403
        ? 'You do not have permission to edit checks in this workspace.'
        : 'Failed to save this check. Your changes are still on screen — try again.');
      throw e;
    }
  }, [entry, draft]);

  const { dirty, saving, touch, flush } = useAutosave(handleSave);

  const handleModeChange = useCallback((next: EditorMode) => {
    if (next === 'edit' && entry) {
      const { title, description, category, severity, pattern, fix, fitnessTest } = entry;
      setDraft({ title, description, category, severity, pattern, fix, fitnessTest });
    }
    setMode(next);
    if (next === 'preview') void flush();
  }, [entry, flush]);

  const updateDraftField = useCallback((field: string, value: string) => {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    touch();
  }, [touch]);

  const handlePublishToCi = useCallback(async () => {
    if (!entry) return;
    await flush();
    setPublish({ phase: 'running' });
    try {
      const jobId = await guardrailsApi.publishSingleToCi(entry.id);
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const status = await guardrailsApi.getJob(jobId);
        if (status.status === 'completed') {
          setPublish({ phase: 'done', prUrl: status.result?.prUrl });
          return;
        }
        if (status.status === 'failed') {
          setPublish({ phase: 'error', message: status.error || 'Publish failed.' });
          return;
        }
      }
      setPublish({ phase: 'error', message: 'Publish timed out — check the fitness-tests repo for the PR.' });
    } catch {
      setPublish({ phase: 'error', message: 'Publish failed. Please try again.' });
    }
  }, [entry, flush]);

  const contest = useCallback(() => {
    if (!entry) return;
    const failing = occurrences.filter((o) => o.check.status === 'failed');
    const failures = failing.map((o) => toCiFailure(o.repo, o.check));
    const evidence = failures.length > 0 ? `\n\n${describeFailures(failures)}` : '';

    navigate('/insights', {
      state: {
        failures,
        issueText:
          `I want to contest the guardrail "${entry.title}" (${guardrailId}).\n\n` +
          `The rule says: ${entry.description}\n\n` +
          `Why I think it is wrong, too broad, or needs an exception here:\n` +
          `<explain — this goes to the people who own the standard>${evidence}`,
      },
    });
  }, [entry, occurrences, navigate, guardrailId]);

  if (loading) {
    return <LoadingState size="lg" />;
  }

  if (notFound || !entry) {
    return (
      <div className="p-8">
        <Link to={{ pathname: '/docs/guardrails', search }} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-4">
          <ArrowLeft className="w-4 h-4" /> All guardrails
        </Link>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
          <h1 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">Guardrail not found</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            <code className="font-mono text-xs">{guardrailId}</code> is not a guardrail in this
            workspace. It may have been removed, or the check that sent you here may belong to a
            different workspace.
          </p>
        </div>
      </div>
    );
  }

  const failing = occurrences.filter((o) => o.check.status === 'failed');
  const shown = mode === 'edit' && draft ? draft : entry;

  return (
    <FullEditorShell
      crumbs={[
        { label: 'Docs', to: { pathname: '/docs', search } },
        { label: 'Checks', icon: ShieldAlert, to: { pathname: '/docs/guardrails', search } },
      ]}
      title={entry.title}
      titleBadges={
        <span className="flex items-center gap-2 min-w-0">
          <AlertTriangle
            className={cn(
              'w-4 h-4 shrink-0',
              shown.severity === 'critical' ? 'text-red-500'
                : shown.severity === 'high' ? 'text-orange-500'
                  : shown.severity === 'medium' ? 'text-amber-500' : 'text-blue-500'
            )}
          />
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium border shrink-0', SEVERITY_COLORS[shown.severity])}>
            {shown.severity}
          </span>
          <span className="text-xs text-slate-500 shrink-0">{shown.category}</span>
          <span className="text-xs text-slate-400 font-mono truncate">{entry.id}</span>
        </span>
      }
      mode={mode}
      onModeChange={handleModeChange}
      dirty={dirty}
      saving={saving}
      onSave={() => void flush()}
      onPublish={ciSettings?.configured ? () => void handlePublishToCi() : undefined}
      publishLabel={publish.phase === 'running' ? 'Publishing…' : 'Publish to CI'}
      publishDisabled={publish.phase === 'running'}
      extraActions={
        <button
          onClick={contest}
          className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-md text-sm font-medium text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30"
        >
          <MessageSquareWarning className="w-4 h-4" />
          Contest
        </button>
      }
    >
      <div className="p-6 space-y-6">
        {saveError && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {saveError}
          </div>
        )}
        {publish.phase === 'done' && (
          <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>
              Published to the fitness-tests repository.{' '}
              {publish.prUrl && (
                <a href={publish.prUrl} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                  View the pull request
                </a>
              )}
            </span>
          </div>
        )}
        {publish.phase === 'error' && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {publish.message}
          </div>
        )}

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Why this check exists</h2>
          {mode === 'edit' && draft ? (
            <EditableGuardrailEntry entry={draft} editing onUpdate={updateDraftField} />
          ) : (
            <GuardrailEntryView entry={entry} />
          )}
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-800">
            Added {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'unknown'}
            {entry.updatedAt && entry.updatedAt !== entry.createdAt
              ? ` · last changed ${new Date(entry.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
              : ''}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
            Where this check is running
          </h2>

          {occurrences.length === 0 ? (
            <p className="text-sm text-slate-500">
              No CI runs have reported this check yet. Once it is published to your fitness-tests
              repository and runs, every result will show up here.
            </p>
          ) : (
            <div className="space-y-2">
              {occurrences.map(({ repo, check }) => (
                <div
                  key={`${repo}-${check.name}`}
                  className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {check.status === 'passed'
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                        : check.status === 'failed'
                          ? <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                          : <Clock className="h-4 w-4 text-slate-400 shrink-0" />}
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{repo}</span>
                      {check.headSha && (
                        <code className="text-[10px] font-mono text-slate-400">{check.headSha.slice(0, 7)}</code>
                      )}
                    </div>
                    {check.status === 'failed' && check.output?.summary && (
                      <pre className="mt-1.5 ml-6 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1.5 whitespace-pre-wrap break-all">
                        {check.output.summary}
                      </pre>
                    )}
                  </div>
                  {check.detailsUrl && (
                    <a
                      href={check.detailsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 shrink-0 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                    >
                      Run <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {failing.length > 0 && (
            <p className="mt-4 text-xs text-slate-500">
              Blocked by this check? Fix it using the correct approach above, or{' '}
              <button onClick={contest} className="text-amber-700 dark:text-amber-300 hover:underline font-medium">
                contest the rule
              </button>{' '}
              — that starts an analysis with this failure attached, so the people who own the standard
              see exactly what you saw.
            </p>
          )}
        </div>
      </div>
    </FullEditorShell>
  );
}
